import { Button } from "@/components/ui/button";
import { API_BASE_URL, tokenManager } from "@/lib/api";
import { cn } from "@/lib/utils";
import * as Sentry from "@sentry/react";
import {
  FileText,
  Image,
  Loader2,
  Music,
  Upload,
  Video,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface FileUploadProps {
  value?: string;
  onChange: (url: string) => void;
  accept?: string;
  label?: string;
  type?: "image" | "audio" | "video" | "file";
  className?: string;
}

export function FileUpload({
  value,
  onChange,
  accept,
  label = "Upload file",
  type = "file",
  className,
}: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  useEffect(() => {
    if (localPreviewUrl) {
      if (!value) {
        URL.revokeObjectURL(localPreviewUrl);
        setLocalPreviewUrl(null);
      } else if (value && !value.startsWith("blob:")) {
        URL.revokeObjectURL(localPreviewUrl);
        setLocalPreviewUrl(null);
      }
    }
  }, [value]);

  const getAcceptTypes = () => {
    if (accept) return accept;
    switch (type) {
      case "image":
        return "image/*";
      case "audio":
        return "audio/*";
      case "video":
        return "video/*";
      default:
        return "*/*";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "image":
        return <Image className="h-8 w-8 text-muted-foreground" />;
      case "audio":
        return <Music className="h-8 w-8 text-muted-foreground" />;
      case "video":
        return <Video className="h-8 w-8 text-muted-foreground" />;
      default:
        return <Upload className="h-8 w-8 text-muted-foreground" />;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }

    const blobUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(blobUrl);

    setIsUploading(true);
    setError(null);

    try {
      const token = tokenManager.get();
      const response = await fetch(`${API_BASE_URL}/uploads/request-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: file.name,
          size: file.size,
          contentType: file.type || "application/octet-stream",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadURL, objectPath } = await response.json();

      const uploadResponse = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      onChange(objectPath);
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          source: "FileUpload.handleFileSelect",
          stage: "submit",
        },
        extra: {
          method: "POST",
          url_path: "/uploads/request-url",
          mediaType: type,
          fileSize: file.size,
          fileMimeType: file.type,
        },
      });
      setError(err instanceof Error ? err.message : "Upload failed");
      URL.revokeObjectURL(blobUrl);
      setLocalPreviewUrl(null);
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleRemove = () => {
    onChange("");
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const previewSrc = localPreviewUrl || value || "";

  const renderPreview = () => {
    if (!value && !localPreviewUrl) return null;

    if (type === "image") {
      return (
        <div className="relative group">
          <img
            src={previewSrc}
            alt="Preview"
            className="h-32 w-full object-cover rounded-md border"
            data-testid="preview-image"
          />
          {isUploading && (
            <div className="absolute inset-0 bg-background/60 flex items-center justify-center rounded-md">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleRemove}
            data-testid="button-remove-upload"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      );
    }

    if (type === "audio") {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-md border">
            <Music className="h-5 w-5 text-muted-foreground shrink-0" />
            <audio
              src={previewSrc}
              controls
              className="flex-1 h-8"
              data-testid="preview-audio"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              onClick={handleRemove}
              data-testid="button-remove-upload"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          {isUploading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Uploading...
            </div>
          )}
        </div>
      );
    }

    if (type === "video") {
      return (
        <div className="relative group">
          <video
            src={previewSrc}
            className="h-32 w-full object-cover rounded-md border"
            controls
            data-testid="preview-video"
          />
          {isUploading && (
            <div className="absolute inset-0 bg-background/60 flex items-center justify-center rounded-md">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleRemove}
            data-testid="button-remove-upload"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-md border">
        <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
        <span className="flex-1 text-sm truncate">{value}</span>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-6 shrink-0"
          onClick={handleRemove}
          data-testid="button-remove-upload"
        >
          <X className="h-3 w-3" />
        </Button>
        {isUploading && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
      </div>
    );
  };

  if (value || localPreviewUrl) {
    return <div className={className}>{renderPreview()}</div>;
  }

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={getAcceptTypes()}
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        className={cn(
          "w-full border-2 border-dashed rounded-lg p-6 flex flex-col items-center gap-2 transition-colors",
          "hover:border-primary hover:bg-muted/50",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
        data-testid="button-upload-trigger"
      >
        {isUploading ? (
          <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
        ) : (
          getIcon()
        )}
        <span className="text-sm text-muted-foreground">
          {isUploading ? "Uploading..." : label}
        </span>
      </button>
      {error && <p className="text-sm text-destructive mt-2">{error}</p>}
    </div>
  );
}

interface MediaPreviewProps {
  url: string | null;
  mediaType: "image" | "audio" | "video";
  onRemove?: () => void;
  onReplace?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  replaceAccept?: string;
  className?: string;
  isReadOnly?: boolean;
}

export function MediaPreview({
  url,
  mediaType,
  onRemove,
  onReplace,
  replaceAccept,
  className,
  isReadOnly = false,
}: MediaPreviewProps) {
  const replaceInputRef = useRef<HTMLInputElement>(null);
  if (!url) return null;

  const replaceControl =
    onReplace && !isReadOnly ? (
      <>
        <input
          ref={replaceInputRef}
          type="file"
          accept={replaceAccept}
          className="hidden"
          onChange={onReplace}
          data-testid="input-replace-media"
        />
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="absolute top-1 right-7 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => replaceInputRef.current?.click()}
          data-testid="button-replace-media"
        >
          <Upload className="h-3 w-3" />
        </Button>
      </>
    ) : null;

  if (mediaType === "image") {
    return (
      <div className={cn("relative group", className)}>
        <img
          src={url}
          alt="Preview"
          className="h-24 w-full object-cover rounded-md border"
          data-testid="preview-media-image"
        />
        {replaceControl}
        {onRemove && !isReadOnly && (
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="absolute top-1 right-1 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={onRemove}
            data-testid="button-remove-media"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  if (mediaType === "audio") {
    return (
      <div
        className={cn(
          "flex items-center gap-2 p-2 bg-muted/50 rounded-md border",
          className,
        )}
      >
        <Music className="h-4 w-4 text-muted-foreground shrink-0" />
        <audio
          key={url}
          src={url}
          controls
          className="flex-1 h-8"
          data-testid="preview-media-audio"
        />
        {onRemove && !isReadOnly && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-5 w-5 shrink-0"
            onClick={onRemove}
            data-testid="button-remove-media"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  if (mediaType === "video") {
    return (
      <div className={cn("relative group", className)}>
        <video
          src={url}
          className="max-h-64 w-full object-contain rounded-md border bg-black"
          controls
          data-testid="preview-media-video"
        />
        {replaceControl}
        {onRemove && !isReadOnly && (
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="absolute top-1 right-1 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={onRemove}
            data-testid="button-remove-media"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return null;
}
